import {delimitedTextFileImporter} from './delimitedTextFileImporter';
import {fastaFileImporter} from './fastaFileImporter';
import {FileImportError, type FileImporter, type FileImportResult, normalizeMimeType} from './FileImport';

const FILE_IMPORTERS: Array<FileImporter> = [delimitedTextFileImporter, fastaFileImporter];

export const ACCEPTED_FILE_TYPES: Record<string, Array<string>> = FILE_IMPORTERS.reduce(
  (acceptedFileTypes, importer) => {
    Object.entries(importer.acceptedFileTypes).forEach(([mimeType, extensions]) => {
      acceptedFileTypes[mimeType] = [...(acceptedFileTypes[mimeType] ?? []), ...extensions];
    });
    return acceptedFileTypes;
  },
  {} as Record<string, Array<string>>
);

function getImporter(file: File): FileImporter | null {
  const normalizedFileName = file.name.toLowerCase();
  const extensionMatch = FILE_IMPORTERS.find((importer) =>
    Object.values(importer.acceptedFileTypes)
      .flat()
      .some((extension) => normalizedFileName.endsWith(extension))
  );
  if (extensionMatch != null) {
    return extensionMatch;
  }

  const normalizedMimeType = normalizeMimeType(file.type);
  return (
    FILE_IMPORTERS.find((importer) =>
      Object.keys(importer.acceptedFileTypes).some(
        (mimeType) => normalizeMimeType(mimeType) === normalizedMimeType
      )
    ) ?? null
  );
}

export async function importFile(
  file: File,
  maxInputs: number,
  usedInputNames?: ReadonlySet<string>
): Promise<FileImportResult> {
  const importer = getImporter(file);
  if (importer == null) {
    throw new FileImportError('Unsupported file type', `"${file.name}" is not a supported input file.`);
  }
  return await importer.importFile(file, maxInputs, usedInputNames);
}
